/**
 * Data Volume hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dataVolumeApi } from '../services/vmApi';
import { DataVolume, DataVolumeCreate } from '../types/dataVolume';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';

export interface UseDataVolumesParams {
  projectId?: string;
  page?: number;
  size?: number;
  enabled?: boolean;
}

export const useDataVolumes = (params: UseDataVolumesParams = {}) => {
  const { projectId, page = 1, size = 20, enabled = true } = params;
  const queryClient = useQueryClient();

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ['data-volumes', projectId, page, size],
    queryFn: () => dataVolumeApi.listVolumes({ project_id: projectId, page, size }),
    enabled: enabled,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    placeholderData: (previousData) => previousData,
  });

  const volumes: DataVolume[] = data?.volumes || [];

  const createMutation = useMutation({
    mutationFn: (volumeData: DataVolumeCreate) => dataVolumeApi.createVolume(volumeData, projectId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['data-volumes'], refetchType: 'active' });
      toast.success('Data Volume creation initiated');
    },
    onError: (error: AxiosError<{ detail?: string; message?: string }>) => {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        'Failed to create Data Volume';
      toast.error(errorMessage);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (volumeId: string) => dataVolumeApi.deleteVolume(volumeId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['data-volumes'], refetchType: 'active' });
      toast.success('Data Volume deletion initiated');
    },
    onError: (error: AxiosError<{ detail?: string; message?: string }>) => {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        'Failed to delete Data Volume';
      toast.error(errorMessage);
    },
  });

  return {
    volumes,
    totalCount: data?.total || 0,
    page: data?.page || page,
    size: data?.size || size,
    pages: data?.pages || 1,
    isLoading,
    isFetching,
    isError,
    refetch,
    createVolume: createMutation.mutate,
    isCreating: createMutation.isPending,
    deleteVolume: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
  };
};

export const useDataVolume = (volumeId: string | undefined) => {
  const queryClient = useQueryClient();

  const { data: volume, isLoading, isError, refetch } = useQuery({
    queryKey: ['data-volume', volumeId],
    queryFn: () => dataVolumeApi.getVolume(volumeId!),
    enabled: !!volumeId,
    staleTime: 0,
    refetchInterval: 10000, // Poll every 10 seconds
  });

  return {
    volume: volume as DataVolume | undefined,
    isLoading,
    isError,
    refetch,
  };
};
